
import { toMapMarkers } from 'utils/trainer-card-projection'
import { useEffect, useState, useCallback } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import Link from 'next/link'
import { countryList, googleApiKey, s3Static } from '../../../utils/constants'
import { getArticleDateTime } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { PersonalTrainerRequestForm } from '../../../components/index/service-category/personal-trainer/PersonalTrainerRequestForm'
import { downloadServiceProviders } from '../../../api/service-provider/sp-list'
import { FaStar, FaSearch, FaCheckCircle, FaTimesCircle, FaUserGraduate, FaClipboardCheck, FaDumbbell, FaChartLine, FaShieldAlt, FaQuestionCircle, FaMapMarkerAlt, FaArrowRight } from 'react-icons/fa'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHealthFitness, getWpNewHealthFitness, setWpHealthFitness, setWpNewHealthFitness } from '../../../service/wp-blogs-store'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { TrainersNeighborhood } from '@/index/service-category/personal-trainer/TrainersNeighborhood'

export async function getStaticProps() {
    let lat = 1.3521
    let lng = 103.8198
    const countryName = "Singapore"
    const { modifiedDate, month, year } = getArticleDateTime();
    const fitnessBlogs = blogsMaster['health-fitness'].blogArray;
    const wpPosts = getWpHealthFitness().length > 0 ? getWpHealthFitness() : await getWpPostsByCategory(6, "health-fitness");
    setWpHealthFitness(wpPosts)
    const wpNewPosts = getWpNewHealthFitness().length > 0 ? getWpNewHealthFitness() : await getWpNewPostsByCategory(6, "health-fitness");
    setWpNewHealthFitness(wpNewPosts)

    const allRequestCategorySettings = await getRequestJSON("Singapore")
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")
    const spListMap = await downloadServiceProviders("Fitness Trainer", 0, 500, "Singapore", lat, lng, 50, "kms", false, false, true)

    let spLocationList = []
    for (let x = 0; x < spListMap.length; x++) {
        let listA = [spListMap[x].userFirstName, spListMap[x].userLatitude, spListMap[x].userLongitude, x + 1]
        spLocationList.push(listA)
    }

    const getLocalityList = () => {
        for (let x in countryList) {
            if (countryList[x].country_name === countryName) {
                return countryList[x].localityList
            }
        }
    }
    const localityList = getLocalityList()

    return {
        props: {
            requestCategorySettings,
            modifiedDate,
            month,
            year,
            fitnessBlogs,
            wpPosts,
            wpNewPosts,
            spListMap: toMapMarkers(spListMap),
            localityList,
            spLocationList
        },
        revalidate: 259200,
    }
}

/* ── Glassmorphism Utility Components ─────────────────────────── */

const GlassCard = ({ children, className = '', glowColor = 'orange', borderColor = 'white/[0.12]' }) => (
    <div className={`relative bg-white/4 backdrop-blur-2xl border border-${borderColor} rounded-3xl shadow-xl shadow-${glowColor}-500/5 ${className}`}>
        {/* Liquid-glass top highlight */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent rounded-t-3xl" />
        {children}
    </div>
)

const GlassBadge = ({ children, color = 'orange' }) => (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-${color}-500/10 text-${color}-400 border border-${color}-500/20 backdrop-blur-sm`}>
        {children}
    </span>
)

const SectionDivider = () => (
    <div className="w-full flex justify-center py-2">
        <div className="w-24 h-px bg-linear-to-r from-transparent via-white/20 to-transparent" />
    </div>
)

/* ── Main Page Component ──────────────────────────────────────── */

export default function PersonalTrainerFinderTool(props) {
    const { modifiedDate, fitnessBlogs, wpPosts, wpNewPosts, requestCategorySettings, spListMap, localityList, spLocationList } = props
    const [displaySp, setDisplaySp] = useState({})

    const trainerDensity = [
        { region: 'East', trainerPercent: 30 },
        { region: 'West', trainerPercent: 10 },
        { region: 'Central', trainerPercent: 40 },
        { region: 'North', trainerPercent: 20 }
    ];

    const initMap = useCallback(async () => {
        const map = new google.maps.Map(document.getElementById('map'), {
            zoom: 11,
            center: new google.maps.LatLng(1.3521, 103.8198),
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            streetViewControl: false,
            mapTypeControl: false,
            mapId: "b76338e454946df6"
        });

        const infoWindow = new google.maps.InfoWindow();

        for (let i = 0; i < spLocationList.length; i++) {
            const position = new google.maps.LatLng(spLocationList[i][1], spLocationList[i][2]);

            const markerContent = document.createElement('div');
            markerContent.style.display = 'flex';
            markerContent.style.flexDirection = 'column';
            markerContent.style.alignItems = 'center';
            markerContent.style.textAlign = 'center';
            markerContent.style.backgroundColor = 'transparent';
            markerContent.style.fontFamily = 'sans-serif';

            const img = document.createElement('img');
            img.src = "/personal-trainer.png";
            img.style.width = '20px';
            img.style.height = '30px';
            img.style.borderRadius = '50%';
            img.style.objectFit = 'cover';
            img.alt = 'Trainer avatar';

            markerContent.appendChild(img);
            markerContent.style.transition = 'transform 0.2s ease';
            markerContent.addEventListener('mouseenter', () => { markerContent.style.transform = 'scale(1.05)'; });
            markerContent.addEventListener('mouseleave', () => { markerContent.style.transform = 'scale(1)'; });

            const marker = new google.maps.marker.AdvancedMarkerElement({ map, position, content: markerContent });

            const contentString =
                '<div id="content">' +
                '<div id="siteNotice"></div>' +
                '<h3 id="firstHeading" class="text-center text-base font-bold pb-2">' + spListMap[i].userFirstName + '</h3>' +
                '<div id="bodyContent" class="w-40">' +
                "<div class='flex'><img class='rounded-lg w-full' src='" + spListMap[i].profilePic + "' layout='fill' height='140px' alt='profile image'/>" +
                "</div>" +
                "<div class='text-center text-xs'>" + spListMap[i].userLocation + "</div>" +
                "</div>" +
                "<div class='text-center py-2'><a class='text-orange-600 font-bold' target='_blank' rel='noreferrer' href='/profile/" + spListMap[i].handleName + "'>View Profile</a></div>" +
                "</div>";

            marker.addListener('gmp-click', () => {
                infoWindow.setContent(contentString);
                infoWindow.open(map);
                infoWindow.setPosition(position);
                setDisplaySp(spListMap[i]);
            });
        }
    }, [spListMap, spLocationList])

    useEffect(() => {
        window.initMap = () => { initMap(); };
        if (window.google && window.google.maps) { initMap(); }
    }, [initMap]);

    return (
        <>
            <Head>
                <title>How to Find a Good Personal Trainer in Singapore | Complete Guide</title>
                <meta charSet="utf-8" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="description" content="Learn how to search and find a good personal trainer in Singapore. A comprehensive guide covering certifications, red flags, interview questions, and proven strategies to choose the right fitness trainer." />
                <meta name="keywords" content="how to find personal trainer singapore, choose good personal trainer, personal trainer certifications singapore, hire personal trainer guide, fitness trainer selection tips, best personal trainer singapore" />
                <meta name="author" content="StringsSG - Personal Training" />
                <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
                <meta property="og:locale" content="en_US" />
                <meta property="og:url" content="https://www.stringssg.com/sg/personal-trainer/personal-trainer-finder-tool" />
                <meta property="og:type" content="article" />
                <meta property="og:title" content="How to Find a Good Personal Trainer in Singapore | Complete Guide" />
                <meta property="og:description" content="Learn how to search and find a good personal trainer in Singapore. Covers certifications, red flags, interview questions, and strategies to choose the right fitness trainer." />
                <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
                <meta property="article:published_time" content="2025-01-08T07:30:46+00:00" />
                <meta property="article:modified_time" content={modifiedDate} />
                <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/fitness-package2.jpg" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/fitness-package2.jpg" />
                <meta name="twitter:site" content="@stringssg" />
                <meta name="twitter:url" content="https://www.stringssg.com/sg/personal-trainer/personal-trainer-finder-tool" />
                <meta name="twitter:title" content="How to Find a Good Personal Trainer in Singapore | Complete Guide" />
                <meta name="twitter:description" content="Learn how to search and find a good personal trainer in Singapore. Covers certifications, red flags, interview questions, and strategies." />
                <link rel="icon" href="/favicon.ico" />
                <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/personal-trainer-finder-tool" />
                <script type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-finder-tool"},"headline":"How to Find a Good Personal Trainer in Singapore | Complete Guide","description":"Learn how to search and find a good personal trainer in Singapore. A comprehensive guide covering certifications, red flags, interview questions, and proven strategies.","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/fitness-package2.jpg"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-08T07:30:46+00:00","dateModified":"${modifiedDate}"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"personal-trainer","item":"https://www.stringssg.com/sg/personal-trainer"},{"@type":"ListItem","position":2,"name":"finder-tool"}]},{"@type":"HowTo","name":"How to Find a Good Personal Trainer in Singapore","step":[{"@type":"HowToStep","name":"Define Your Fitness Goals","text":"Identify what you want to achieve: weight loss, muscle building, sports performance, or rehabilitation."},{"@type":"HowToStep","name":"Research Certifications","text":"Look for trainers with reputable certifications like NSCA-CSCS, ACSM, ACE, or NASM."},{"@type":"HowToStep","name":"Check Track Record","text":"Review before/after photos, client testimonials, and years of experience."},{"@type":"HowToStep","name":"Interview the Trainer","text":"Ask about their approach to nutrition, programming, and how they handle plateaus."},{"@type":"HowToStep","name":"Book a Trial Session","text":"Test the trainer with a trial session before committing to a package."}]}]}`
                    }}
                />
            </Head>

            {/* ── Ambient Background ── */}
            <div className="relative min-h-screen bg-slate-950">
                {/* Floating ambient orbs */}
                <div className="fixed inset-0 pointer-events-none overflow-hidden">
                    <div className="absolute top-[-10%] left-[-5%] w-125 h-125 rounded-full bg-orange-500/[0.07] blur-[120px]" />
                    <div className="absolute top-[30%] right-[-10%] w-150 h-150 rounded-full bg-amber-400/5 blur-[140px]" />
                    <div className="absolute bottom-[10%] left-[20%] w-100 h-100 rounded-full bg-sky-500/4 blur-[100px]" />
                    <div className="absolute top-[60%] right-[30%] w-75 h-75 rounded-full bg-rose-500/4 blur-[80px]" />
                </div>

                {/* Request Form Banner */}
                <PersonalTrainerRequestForm requestCategorySettings={requestCategorySettings} />

                {/* ════════════════════════════════════════════════════════════════
                    SECTION 1 — HERO: How to Find a Good Personal Trainer
                ════════════════════════════════════════════════════════════════ */}
                <section className="relative px-4 pt-16 pb-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <GlassBadge color="orange">
                            <FaSearch className="text-[10px]" /> Complete Guide
                        </GlassBadge>

                        <h1 className="mt-6 text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
                            How to Search &amp; Find a{' '}
                            <span className="bg-linear-to-r from-orange-400 via-amber-400 to-orange-500 bg-clip-text text-transparent">
                                Good Personal Trainer
                            </span>{' '}
                            in Singapore
                        </h1>

                        <p className="mt-5 text-base md:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed">
                            Hiring the wrong trainer wastes your money and time. This guide teaches you exactly what to look for,
                            what to avoid, and how to evaluate trainers before committing — so every dollar you spend delivers real results.
                        </p>

                        {/* Quick nav pills */}
                        <div className="mt-8 flex flex-wrap justify-center gap-2">
                            {[
                                { label: 'Where to Search', href: '#where-to-search' },
                                { label: 'Green & Red Flags', href: '#quality-signals' },
                                { label: 'Certifications', href: '#certifications' },
                                { label: 'Interview Questions', href: '#interview-questions' },
                                { label: 'Trainer Map', href: '#trainer-map' },
                            ].map((pill) => (
                                <a
                                    key={pill.href}
                                    href={pill.href}
                                    className="px-4 py-2 rounded-full text-xs font-medium bg-white/6 text-white/60 border border-white/8 backdrop-blur-sm hover:bg-white/12 hover:text-white/90 transition-all duration-300"
                                >
                                    {pill.label}
                                </a>
                            ))}
                        </div>
                    </div>
                </section>

                <SectionDivider />

                {/* ════════════════════════════════════════════════════════════════
                    SECTION 2 — WHERE TO SEARCH
                ════════════════════════════════════════════════════════════════ */}
                <section id="where-to-search" className="relative px-4 py-14">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-10">
                            <GlassBadge color="sky">
                                <FaSearch className="text-[10px]" /> Step 1
                            </GlassBadge>
                            <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                                Where to Search for Personal Trainers in Singapore
                            </h2>
                            <p className="mt-3 text-sm md:text-base text-white/40 max-w-2xl mx-auto">
                                Singapore has hundreds of trainers. Here are the most effective channels to find one — ranked by reliability.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* Channel 1: StringsSG */}
                            <div className="group relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-lg shadow-orange-500/5 hover:border-orange-400/30 transition-all duration-500">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent rounded-t-3xl" />
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/20 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                        <span className="text-xl">🏆</span>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-bold text-white/90 text-lg">StringsSG Platform</h3>
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/20">Recommended</span>
                                        </div>
                                        <p className="text-sm text-white/45 leading-relaxed">
                                            Browse verified, full-time personal trainers across Singapore with transparent reviews, certifications, and pricing.
                                            Post a request with your goals and get matched automatically.
                                        </p>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">✅ Verified Profiles</span>
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">✅ Free Trial Session</span>
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">✅ Real Reviews</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Channel 2: Google */}
                            <div className="group relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-lg hover:border-sky-400/20 transition-all duration-500">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent rounded-t-3xl" />
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-sky-500/20 to-blue-500/20 border border-sky-500/20 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                        <span className="text-xl">🔍</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-white/90 text-lg mb-1">Google Search &amp; Maps</h3>
                                        <p className="text-sm text-white/45 leading-relaxed">
                                            Search &quot;personal trainer Singapore&quot; on Google Maps to find gyms and freelance trainers.
                                            Check Google reviews, but verify credentials independently as reviews can be manipulated.
                                        </p>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">⚠️ Verify Reviews</span>
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">✅ Large Pool</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Channel 3: Referrals */}
                            <div className="group relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-lg hover:border-emerald-400/20 transition-all duration-500">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent rounded-t-3xl" />
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/20 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                        <span className="text-xl">🤝</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-white/90 text-lg mb-1">Personal Referrals</h3>
                                        <p className="text-sm text-white/45 leading-relaxed">
                                            Ask friends, colleagues, or family who have achieved visible fitness results.
                                            A referral from someone with similar goals is the most trustworthy signal.
                                        </p>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">✅ Trusted Source</span>
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">⚠️ Limited Options</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Channel 4: Gyms */}
                            <div className="group relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-lg hover:border-purple-400/20 transition-all duration-500">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent rounded-t-3xl" />
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-purple-500/20 to-violet-500/20 border border-purple-500/20 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                        <span className="text-xl">🏋️</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-white/90 text-lg mb-1">Walk into a Local Gym</h3>
                                        <p className="text-sm text-white/45 leading-relaxed">
                                            Visit gyms near you and speak to staff about trainers who match your goals.
                                            Note: gym memberships ($170+/month) are usually required on top of training fees.
                                        </p>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">✅ Meet In Person</span>
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">⚠️ Extra Membership Cost</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Channel 5: Social Media */}
                            <div className="group relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-lg hover:border-pink-400/20 transition-all duration-500">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent rounded-t-3xl" />
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-pink-500/20 to-rose-500/20 border border-pink-500/20 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                        <span className="text-xl">📱</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-white/90 text-lg mb-1">Social Media &amp; Communities</h3>
                                        <p className="text-sm text-white/45 leading-relaxed">
                                            Instagram, TikTok, and Reddit Singapore fitness communities can surface trainers.
                                            Be cautious — flashy content doesn&apos;t equal teaching ability. Check credentials separately.
                                        </p>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">⚠️ Verify Credentials</span>
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">✅ See Content Style</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Channel 6: Marketplace Apps */}
                            <div className="group relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-lg hover:border-amber-400/20 transition-all duration-500">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent rounded-t-3xl" />
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-amber-500/20 to-yellow-500/20 border border-amber-500/20 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                        <span className="text-xl">📋</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-white/90 text-lg mb-1">Marketplace Apps (Carousell, etc.)</h3>
                                        <p className="text-sm text-white/45 leading-relaxed">
                                            General marketplaces list freelance trainers at competitive rates.
                                            However, there is no vetting process — always verify certifications and request references before hiring.
                                        </p>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">✅ Competitive Rates</span>
                                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/6 text-white/40">⚠️ No Vetting</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <SectionDivider />

                {/* ════════════════════════════════════════════════════════════════
                    SECTION 3 — QUALITY SIGNALS (Green & Red Flags)
                ════════════════════════════════════════════════════════════════ */}
                <section id="quality-signals" className="relative px-4 py-14">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-10">
                            <GlassBadge color="sky">
                                <FaShieldAlt className="text-[10px]" /> Step 2
                            </GlassBadge>
                            <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                                How to Identify a <span className="text-orange-400">Good</span> Personal Trainer
                            </h2>
                            <p className="mt-3 text-sm md:text-base text-white/40 max-w-2xl mx-auto">
                                Not all trainers are equal. A cheap trainer who doesn&apos;t deliver results costs more than a great one who transforms your body in 3 months. Here&apos;s what separates the best from the rest.
                            </p>
                        </div>

                        {/* 4 Quality Pillars */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* Pillar 1: Nutrition */}
                            <div className="relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-xl shadow-green-500/5">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-green-400/20 to-transparent rounded-t-3xl" />
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-linear-to-br from-green-500/20 to-emerald-500/20 border border-green-500/20 flex items-center justify-center">
                                        <span className="text-lg">🍎</span>
                                    </div>
                                    <h3 className="font-bold text-white/90 text-lg">Nutrition Guidance Quality</h3>
                                </div>
                                <p className="text-sm text-white/45 mb-4">
                                    <strong className="text-white/70">Diet is 80% of your results.</strong> A trainer who only focuses on workouts is leaving you without the most important piece of the puzzle.
                                </p>
                                <div className="space-y-2">
                                    <div className="bg-red-500/8 p-3 rounded-xl border border-red-500/10">
                                        <p className="text-sm text-white/55"><FaTimesCircle className="inline text-red-400 mr-1.5" /><strong className="text-red-400">Bad:</strong> &quot;Just drink more water&quot; or &quot;Eat more fruits&quot;</p>
                                    </div>
                                    <div className="bg-green-500/8 p-3 rounded-xl border border-green-500/10">
                                        <p className="text-sm text-white/55"><FaCheckCircle className="inline text-green-400 mr-1.5" /><strong className="text-green-400">Good:</strong> &quot;Let me analyze your diet, calculate macros, and adjust for your lifestyle&quot;</p>
                                    </div>
                                </div>
                            </div>

                            {/* Pillar 2: Programming */}
                            <div className="relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-xl shadow-blue-500/5">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-blue-400/20 to-transparent rounded-t-3xl" />
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-linear-to-br from-blue-500/20 to-sky-500/20 border border-blue-500/20 flex items-center justify-center">
                                        <span className="text-lg">📋</span>
                                    </div>
                                    <h3 className="font-bold text-white/90 text-lg">Programming Quality</h3>
                                </div>
                                <p className="text-sm text-white/45 mb-4">
                                    <strong className="text-white/70">A good trainer teaches you independence.</strong> Their goal should be to eventually work themselves out of a job with you.
                                </p>
                                <div className="space-y-2">
                                    <div className="bg-red-500/8 p-3 rounded-xl border border-red-500/10">
                                        <p className="text-sm text-white/55"><FaTimesCircle className="inline text-red-400 mr-1.5" /><strong className="text-red-400">Red Flag:</strong> Same workout every session, no progression, keeps you dependent</p>
                                    </div>
                                    <div className="bg-green-500/8 p-3 rounded-xl border border-green-500/10">
                                        <p className="text-sm text-white/55"><FaCheckCircle className="inline text-green-400 mr-1.5" /><strong className="text-green-400">Green Flag:</strong> Progressive overload, teaches proper form, gives homework sessions</p>
                                    </div>
                                </div>
                            </div>

                            {/* Pillar 3: Form Correction */}
                            <div className="relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-xl shadow-purple-500/5">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-purple-400/20 to-transparent rounded-t-3xl" />
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-linear-to-br from-purple-500/20 to-violet-500/20 border border-purple-500/20 flex items-center justify-center">
                                        <span className="text-lg">📐</span>
                                    </div>
                                    <h3 className="font-bold text-white/90 text-lg">Form Correction Ability</h3>
                                </div>
                                <p className="text-sm text-white/45 mb-4">
                                    <strong className="text-white/70">This is THE main value of a PT.</strong> Poor form leads to injuries and wasted effort. A skilled trainer spots micro-corrections others miss entirely.
                                </p>
                                <div className="space-y-2">
                                    <p className="text-sm text-white/45">Ask: &quot;What are the 3 most common form mistakes in [exercise]?&quot;</p>
                                    <div className="bg-green-500/8 p-3 rounded-xl border border-green-500/10">
                                        <p className="text-sm text-white/55"><FaCheckCircle className="inline text-green-400 mr-1.5" /> Good trainers immediately list specific errors and demonstrate corrections</p>
                                    </div>
                                </div>
                            </div>

                            {/* Pillar 4: Progress Tracking */}
                            <div className="relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-xl shadow-amber-500/5">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-amber-400/20 to-transparent rounded-t-3xl" />
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-linear-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/20 flex items-center justify-center">
                                        <span className="text-lg">📊</span>
                                    </div>
                                    <h3 className="font-bold text-white/90 text-lg">Progress Tracking</h3>
                                </div>
                                <p className="text-sm text-white/45 mb-4">
                                    <strong className="text-white/70">What gets measured gets managed.</strong> Professional trainers track everything meticulously.
                                </p>
                                <div className="bg-green-500/8 p-3 rounded-xl border border-green-500/10">
                                    <p className="text-sm mb-2"><strong className="text-green-400"><FaCheckCircle className="inline mr-1.5" />Good trainers track:</strong></p>
                                    <ul className="list-disc pl-5 text-sm text-white/50 space-y-1">
                                        <li>Body measurements &amp; photos (monthly)</li>
                                        <li>Workout weights &amp; reps (every session)</li>
                                        <li>Energy levels &amp; sleep quality</li>
                                        <li>Dietary adherence &amp; adjustments</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Comprehensive Red Flags Glassmorphism Card */}
                        <div className="mt-8 relative bg-white/3 backdrop-blur-2xl border border-red-400/15 rounded-3xl p-6 md:p-8 shadow-xl shadow-red-500/5">
                            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-red-400/25 to-transparent rounded-t-3xl" />
                            <h3 className="text-xl font-bold text-red-400 mb-5 flex items-center gap-2">
                                <FaTimesCircle /> Red Flags to Watch Out For
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                    "No certifications or refuses to share them",
                                    "Pushes long-term contracts upfront (50+ sessions)",
                                    "Uses high-pressure sales tactics",
                                    "Cannot explain the science behind exercises",
                                    "One-size-fits-all program for every client",
                                    "No initial fitness assessment or health screening",
                                    "Checks phone constantly during sessions",
                                    "Makes unrealistic promises (\"6-pack in 2 weeks\")",
                                    "No nutrition guidance whatsoever",
                                    "Gets offended when you ask questions"
                                ].map((flag, i) => (
                                    <div key={i} className="flex items-start gap-2 bg-red-500/6 rounded-xl px-3 py-2.5 border border-red-500/10">
                                        <FaTimesCircle className="text-red-400 mt-0.5 shrink-0 text-xs" />
                                        <span className="text-sm text-white/55">{flag}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Green Flags Card */}
                        <div className="mt-5 relative bg-white/3 backdrop-blur-2xl border border-green-400/15 rounded-3xl p-6 md:p-8 shadow-xl shadow-green-500/5">
                            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-green-400/25 to-transparent rounded-t-3xl" />
                            <h3 className="text-xl font-bold text-green-400 mb-5 flex items-center gap-2">
                                <FaCheckCircle /> Green Flags of a Great Trainer
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                    "Conducts a thorough health & fitness assessment first",
                                    "Creates a customized program based on YOUR goals",
                                    "Explains the 'why' behind each exercise",
                                    "Provides detailed nutrition guidance & meal planning",
                                    "Tracks your progress with data and measurements",
                                    "Has verifiable client transformations (before/after)",
                                    "Holds multiple reputable certifications",
                                    "Continues their own education and training",
                                    "Offers a trial session before committing",
                                    "Encourages you to eventually train independently"
                                ].map((flag, i) => (
                                    <div key={i} className="flex items-start gap-2 bg-green-500/6 rounded-xl px-3 py-2.5 border border-green-500/10">
                                        <FaCheckCircle className="text-green-400 mt-0.5 shrink-0 text-xs" />
                                        <span className="text-sm text-white/55">{flag}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <SectionDivider />

                {/* ════════════════════════════════════════════════════════════════
                    SECTION 4 — CERTIFICATIONS BREAKDOWN
                ════════════════════════════════════════════════════════════════ */}
                <section id="certifications" className="relative px-4 py-14">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-10">
                            <GlassBadge color="rose">
                                <FaUserGraduate className="text-[10px]" /> Step 3
                            </GlassBadge>
                            <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                                Personal Training Certifications Explained
                            </h2>
                            <p className="mt-3 text-sm md:text-base text-white/40 max-w-2xl mx-auto">
                                Understanding certifications helps you separate truly qualified trainers from those with weekend courses. Here&apos;s what each means for your training quality.
                            </p>
                        </div>

                        <div className="relative bg-white/3 backdrop-blur-2xl border border-white/10 rounded-3xl overflow-hidden shadow-xl">
                            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent" />
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse text-sm text-white/55">
                                    <thead>
                                        <tr className="bg-linear-to-r from-orange-500/20 to-amber-500/20 backdrop-blur-sm">
                                            <th className="border-b border-white/8 p-4 text-left text-white/80 font-semibold">Certification</th>
                                            <th className="border-b border-white/8 p-4 text-center text-white/80 font-semibold">Prestige</th>
                                            <th className="border-b border-white/8 p-4 text-center text-white/80 font-semibold">Rate Premium</th>
                                            <th className="border-b border-white/8 p-4 text-white/80 font-semibold">What It Means</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { cert: 'NSCA-CSCS', stars: '⭐⭐⭐⭐⭐', premium: '+30-50%', premiumColor: 'text-emerald-400', desc: 'Gold standard. Requires degree + rigorous exam. Sports performance specialists.' },
                                            { cert: 'ACSM', stars: '⭐⭐⭐⭐⭐', premium: '+25-40%', premiumColor: 'text-emerald-400', desc: 'Medical-grade. Clinical exercise physiology. Ideal for health conditions.' },
                                            { cert: 'ACE', stars: '⭐⭐⭐⭐', premium: '+15-25%', premiumColor: 'text-emerald-400', desc: 'Widely recognized. Strong behavior change focus. Good all-around cert.' },
                                            { cert: 'NASM', stars: '⭐⭐⭐⭐', premium: '+15-25%', premiumColor: 'text-emerald-400', desc: 'Excellent corrective exercise approach. Great for injury prevention.' },
                                            { cert: 'ISSA', stars: '⭐⭐⭐', premium: '+10-15%', premiumColor: 'text-emerald-400', desc: 'Online certification. Comprehensive but less rigorous testing.' },
                                            { cert: 'Republic Polytechnic / ITE (SG)', stars: '⭐⭐⭐', premium: 'Standard', premiumColor: 'text-white/50', desc: 'Local Singapore qualification. Good foundation, often combined with others.' },
                                            { cert: 'AFA (Australian Fitness Academy)', stars: '⭐⭐⭐', premium: 'Standard', premiumColor: 'text-white/50', desc: 'Popular in Singapore. Practical focus with good gym-floor skills.' },
                                            { cert: 'Weekend/Online-only Certs', stars: '⭐', premium: 'Should be LOWER', premiumColor: 'text-red-400', desc: '⚠️ Be cautious. Quick certs often lack depth and practical training.' },
                                        ].map((row, i) => (
                                            <tr key={i} className={`${i % 2 === 0 ? 'bg-white/2' : 'bg-transparent'} hover:bg-white/4 transition-colors`}>
                                                <td className="border-b border-white/5 p-4 font-semibold text-white/75">{row.cert}</td>
                                                <td className="border-b border-white/5 p-4 text-center text-xs">{row.stars}</td>
                                                <td className={`border-b border-white/5 p-4 text-center font-medium ${row.premiumColor}`}>{row.premium}</td>
                                                <td className="border-b border-white/5 p-4 text-xs text-white/45">{row.desc}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-6 py-4 bg-white/2 border-t border-white/6">
                                <p className="text-xs text-white/35">
                                    <strong className="text-white/55">💡 Pro Tip:</strong> Look for trainers with <strong className="text-white/60">multiple certifications</strong> — it shows commitment to ongoing education. Also check for <strong className="text-white/60">specialty certifications</strong> matching your goal (e.g., pre/post natal, senior fitness, sports-specific).
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <SectionDivider />

                {/* ════════════════════════════════════════════════════════════════
                    SECTION 5 — INTERVIEW QUESTIONS
                ════════════════════════════════════════════════════════════════ */}
                <section id="interview-questions" className="relative px-4 py-14">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-10">
                            <GlassBadge color="amber">
                                <FaQuestionCircle className="text-[10px]" /> Step 4
                            </GlassBadge>
                            <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                                Questions to Ask Before Hiring a Trainer
                            </h2>
                            <p className="mt-3 text-sm md:text-base text-white/40 max-w-2xl mx-auto">
                                Treat this like a job interview — because you&apos;re hiring someone to help transform your body. A good trainer will welcome these questions.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {[
                                {
                                    q: "Can you show me before/after photos of past clients with similar goals?",
                                    why: "Proves they've actually delivered results, not just talked about them.",
                                    icon: "📸",
                                    accent: "orange"
                                },
                                {
                                    q: "How will you adjust my nutrition based on my current eating habits?",
                                    why: "Tests if they provide real nutritional coaching or just generic advice.",
                                    icon: "🍽️",
                                    accent: "green"
                                },
                                {
                                    q: "What's your plan if I plateau after 4-6 weeks?",
                                    why: "Reveals if they have strategies for when things get tough — and they will.",
                                    icon: "📈",
                                    accent: "sky"
                                },
                                {
                                    q: "How do you structure programs for clients to eventually train independently?",
                                    why: "A trainer who wants you dependent forever isn't working in your interest.",
                                    icon: "🎯",
                                    accent: "purple"
                                },
                                {
                                    q: "What certifications do you hold, and when did you last update your education?",
                                    why: "Fitness science evolves. Good trainers stay current with continuing education.",
                                    icon: "📜",
                                    accent: "rose"
                                },
                                {
                                    q: "What does a typical first session with a new client look like?",
                                    why: "Should include health screening, fitness assessment, and goal discussion — not just a workout.",
                                    icon: "🏁",
                                    accent: "amber"
                                },
                                {
                                    q: "How do you handle clients with injuries or health conditions?",
                                    why: "Tests their knowledge of modifications and safety protocols.",
                                    icon: "🩺",
                                    accent: "emerald"
                                },
                                {
                                    q: "Can I speak with 1-2 of your current or past clients?",
                                    why: "Nothing beats direct testimonials. A confident trainer will happily share references.",
                                    icon: "🗣️",
                                    accent: "blue"
                                }
                            ].map((item, i) => (
                                <div key={i} className="group relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-5 hover:border-white/18 transition-all duration-500">
                                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/15 to-transparent rounded-t-3xl" />
                                    <div className="flex items-start gap-3">
                                        <span className="text-2xl mt-0.5">{item.icon}</span>
                                        <div>
                                            <p className="text-sm font-semibold text-white/80 mb-2 leading-snug">&quot;{item.q}&quot;</p>
                                            <p className="text-xs text-white/40 leading-relaxed">
                                                <span className="text-white/55 font-medium">Why ask this:</span> {item.why}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <SectionDivider />

                {/* ════════════════════════════════════════════════════════════════
                    SECTION 6 — 5-STEP EVALUATION FRAMEWORK
                ════════════════════════════════════════════════════════════════ */}
                <section className="relative px-4 py-14">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-10">
                            <GlassBadge color="sky">
                                <FaClipboardCheck className="text-[10px]" /> Step 5
                            </GlassBadge>
                            <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                                Your 5-Step Evaluation Framework
                            </h2>
                            <p className="mt-3 text-sm text-white/40 max-w-xl mx-auto">
                                Follow this step-by-step process to confidently choose the right trainer for your goals and budget.
                            </p>
                        </div>

                        <div className="relative">
                            {/* Vertical glass connector line */}
                            <div className="absolute left-6 md:left-8 top-6 bottom-6 w-px bg-linear-to-b from-orange-400/30 via-sky-400/20 to-purple-400/30" />

                            <div className="space-y-5">
                                {[
                                    {
                                        step: 1,
                                        title: "Define Your Goals Clearly",
                                        desc: "Weight loss? Muscle gain? Marathon prep? Rehabilitation? The clearer your goal, the better you can match with a specialist. Write down specific targets with timelines.",
                                        gradient: "from-orange-500 to-amber-500"
                                    },
                                    {
                                        step: 2,
                                        title: "Shortlist 3-5 Trainers",
                                        desc: "Use the channels above to create a shortlist. Check profiles, certifications, reviews, and specializations. Don't settle for the first option.",
                                        gradient: "from-sky-500 to-blue-500"
                                    },
                                    {
                                        step: 3,
                                        title: "Interview & Ask the Hard Questions",
                                        desc: "Use the questions above. Pay attention to how they respond — do they get defensive or welcome your curiosity? Communication style matters.",
                                        gradient: "from-emerald-500 to-green-500"
                                    },
                                    {
                                        step: 4,
                                        title: "Book a Trial Session",
                                        desc: "Never commit to a full package without a trial. Observe: Do they assess your fitness first? Correct your form? Explain exercises? A trial reveals everything.",
                                        gradient: "from-purple-500 to-violet-500"
                                    },
                                    {
                                        step: 5,
                                        title: "Evaluate After 4-6 Sessions",
                                        desc: "After the initial block, assess: Have you learned anything new? Is your form improving? Do they track your progress? Are you seeing measurable results? If not, switch.",
                                        gradient: "from-rose-500 to-pink-500"
                                    }
                                ].map((item) => (
                                    <div key={item.step} className="relative pl-14 md:pl-20">
                                        {/* Step number badge */}
                                        <div className={`absolute left-2 md:left-4 top-4 w-8 h-8 rounded-full bg-linear-to-br ${item.gradient} flex items-center justify-center shadow-lg`}>
                                            <span className="text-white text-sm font-bold">{item.step}</span>
                                        </div>
                                        <div className="relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-2xl p-5 hover:border-white/16 transition-all duration-300">
                                            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/15 to-transparent rounded-t-2xl" />
                                            <h3 className="font-bold text-white/85 text-base mb-1.5">{item.title}</h3>
                                            <p className="text-sm text-white/45 leading-relaxed">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <SectionDivider />

                {/* ════════════════════════════════════════════════════════════════
                    SECTION 7 — TRAINER DENSITY + MAP
                ════════════════════════════════════════════════════════════════ */}
                <section id="trainer-map" className="relative px-4 py-14">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-8">
                            <GlassBadge color="orange">
                                <FaMapMarkerAlt className="text-[10px]" /> Explore
                            </GlassBadge>
                            <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                                {spListMap.length} Personal Trainers Across Singapore
                            </h2>
                            <p className="mt-3 text-sm text-white/40 max-w-xl mx-auto">
                                Use our interactive map to explore trainer availability by area. Tap any marker to view their profile, certifications, and reviews.
                            </p>
                        </div>

                        {/* Trainer Distribution Chart in Glass */}
                        <div className="relative bg-white/4 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 mb-8 shadow-xl">
                            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent rounded-t-3xl" />
                            <h3 className="text-sm font-semibold text-center text-white/60 mb-4">
                                Trainer Distribution Across Singapore Regions
                            </h3>
                            <div className="h-56 max-w-md mx-auto">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={trainerDensity}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                        <XAxis dataKey="region" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                                        <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                                        <Tooltip
                                            formatter={(v) => [`${v}%`, 'Trainer Density']}
                                            contentStyle={{ background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', backdropFilter: 'blur(12px)' }}
                                            labelStyle={{ color: 'rgba(255,255,255,0.7)' }}
                                            itemStyle={{ color: '#fb923c' }}
                                        />
                                        <Bar dataKey="trainerPercent" fill="url(#barGradient)" name="Trainer %" radius={[8, 8, 0, 0]} />
                                        <defs>
                                            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#f97316" />
                                                <stop offset="100%" stopColor="#f59e0b" />
                                            </linearGradient>
                                        </defs>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <p className="text-xs text-white/30 text-center mt-3">
                                Based on StringsSG platform data. Central region has the highest concentration of active trainers.
                            </p>
                        </div>

                        {/* Map */}
                        <div className="relative bg-white/4 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
                            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent" />
                            <div id="map" className="h-136" />
                        </div>

                        {/* Selected Trainer Preview */}
                        {displaySp?.userFirstName && (
                            <div className="mt-5 relative bg-white/5 backdrop-blur-2xl border border-orange-400/20 rounded-2xl p-5 shadow-lg shadow-orange-500/10 animate-fadeIn">
                                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-orange-400/30 to-transparent rounded-t-2xl" />
                                <div className="flex items-center gap-4">
                                    {displaySp.profilePic && (
                                        <div className="relative w-16 h-16 rounded-2xl overflow-hidden border border-white/10 shrink-0">
                                            <Image src={displaySp.profilePic} fill style={{ objectFit: 'cover' }} alt={displaySp.userFirstName} />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-bold text-white/90">{displaySp.userFirstName}</h4>
                                        <p className="text-xs text-white/40 truncate">{displaySp.userLocation}</p>
                                        {displaySp.averageRating > 0 && (
                                            <div className="flex items-center gap-1 mt-1">
                                                {[1, 2, 3, 4, 5].map((s) => (
                                                    <FaStar key={s} className={`text-xs ${s <= displaySp.averageRating ? 'text-amber-400' : 'text-white/10'}`} />
                                                ))}
                                                <span className="text-xs text-white/40 ml-1">({displaySp.averageRating})</span>
                                            </div>
                                        )}
                                    </div>
                                    <Link
                                        href={`/profile/${displaySp.handleName}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="px-4 py-2 rounded-full bg-linear-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold hover:from-orange-600 hover:to-amber-600 transition-all shrink-0"
                                    >
                                        View Profile
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                <SectionDivider />

                {/* ════════════════════════════════════════════════════════════════
                    SECTION 8 — CTA: READY TO START?
                ════════════════════════════════════════════════════════════════ */}
                <section className="relative px-4 py-16">
                    <div className="max-w-3xl mx-auto">
                        <div className="relative bg-linear-to-br from-orange-500/12 to-amber-500/8 backdrop-blur-2xl border border-orange-400/20 rounded-3xl p-8 md:p-12 text-center shadow-2xl shadow-orange-500/10">
                            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-orange-300/30 to-transparent rounded-t-3xl" />
                            {/* Decorative inner glow */}
                            <div className="pointer-events-none absolute inset-0 rounded-3xl bg-linear-to-br from-white/4 to-transparent" />

                            <div className="relative">
                                <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight mb-4">
                                    Ready to Find Your Perfect Trainer?
                                </h2>
                                <p className="text-white/50 mb-8 max-w-lg mx-auto leading-relaxed">
                                    Apply what you&apos;ve learned. Browse our {spListMap.length} verified personal trainers in Singapore,
                                    or post a request and let us match you with the ideal trainer for your goals.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <Link
                                        href="/sg/personal-trainer"
                                        className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-linear-to-r from-orange-500 to-amber-500 text-white font-semibold hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-lg shadow-orange-500/25"
                                    >
                                        Claim Free Consultation <FaArrowRight className="text-xs" />
                                    </Link>
                                    <Link
                                        href="/sg/personal-trainer/trainer-list/Singapore"
                                        prefetch={false}
                                        className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white/80 font-semibold hover:bg-white/6 hover:border-white/30 transition-all duration-300 backdrop-blur-sm"
                                    >
                                        Browse All Trainers
                                    </Link>
                                </div>
                                <p className="mt-6 text-xs text-white/30">
                                    Looking for trainers nearby? <Link href="/sg/personal-trainer/nearby" className="text-orange-400/70 hover:text-orange-400 underline underline-offset-2">Find personal trainers near your location →</Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ════════════════════════════════════════════════════════════════
                    SECTION 9 — NEIGHBORHOOD LINKS + BLOGS
                ════════════════════════════════════════════════════════════════ */}
                <TrainersNeighborhood localityList={localityList} />
                <BlogsList blogs={fitnessBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts} theme="dark" />
            </div>

            {/* ── Scripts ── */}
            <Script
                id="google-tagmanager-new"
                src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
                strategy="lazyOnload"
                onLoad={() => {
                    window.dataLayer = window.dataLayer || [];
                    function gtag() { dataLayer.push(arguments); }
                    gtag('js', new Date());
                    gtag('config', 'G-QD1HGGHDYJ');
                }}
            />
            <Script
                id="google-maps"
                src={`https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&language=en&libraries=places,marker&callback=initMap&loading=async`}
                strategy="afterInteractive"
            />
            <Script id="facebook-tracking" strategy="lazyOnload">
                {`!function(f,b,e,v,n,t,s)
                    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                    n.queue=[];t=b.createElement(e);t.async=!0;
                    t.src=v;s=b.getElementsByTagName(e)[0];
                    s.parentNode.insertBefore(t,s)}(window, document,'script',
                    'https://connect.facebook.net/en_US/fbevents.js');
                    fbq('init', '128634041116395');
                    fbq('track', 'PageView');`}
            </Script>
        </>
    )
}
